/**
 * Maps Supabase auth errors to user-friendly, actionable messages.
 *
 * Shared between web and mobile so the wording stays consistent. Avoids
 * surfacing raw Supabase strings like "Invalid login credentials" — those
 * leak account-existence hints and read as engineer-speak in the UI.
 */
const DEV_HINT = " Local dev: check Supabase Studio or run `pnpm db:reset` if you recently reset the DB.";
function isDevEnv() {
    // Resolved lazily so the hint follows the runtime's NODE_ENV (works on web SSR,
    // web client, and React Native — RN's __DEV__ also lifts NODE_ENV to "development").
    try {
        return (typeof process !== "undefined" &&
            process.env?.NODE_ENV === "development");
    }
    catch {
        return false;
    }
}
function appendDevHint(message) {
    return isDevEnv() ? `${message}${DEV_HINT}` : message;
}
export function mapAuthError(error) {
    if (!error)
        return "Something went wrong. Please try again.";
    const message = typeof error === "string" ? error : (error.message ?? "").trim();
    const code = typeof error === "string" ? undefined : String(error.code ?? "");
    const normalized = message.toLowerCase();
    if (code === "invalid_credentials" ||
        normalized.includes("invalid login credentials") ||
        normalized.includes("invalid credentials")) {
        return appendDevHint("Email or password is incorrect. Try again or reset your password.");
    }
    if (code === "email_not_confirmed" ||
        normalized.includes("email not confirmed") ||
        normalized.includes("confirm your email")) {
        return "Confirm your email before signing in. Check your inbox.";
    }
    if (code === "over_request_rate_limit" ||
        normalized.includes("rate limit") ||
        normalized.includes("too many requests")) {
        return "Too many attempts. Wait a moment and try again.";
    }
    if (normalized.includes("user already registered") ||
        normalized.includes("already been registered")) {
        return "An account with this email already exists. Try signing in instead.";
    }
    if (normalized.includes("password should be at least")) {
        return "Password must be at least 6 characters.";
    }
    if (normalized.includes("signup is disabled")) {
        return "Sign up is currently unavailable. Please contact support.";
    }
    if (message)
        return message;
    return "Something went wrong. Please try again.";
}
